import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaSpinner, FaMapMarkerAlt, FaUtensils, FaLandmark, FaTree, FaStore, FaMonument, FaExternalLinkAlt } from 'react-icons/fa';
import { exploreBetweenPoints } from '../api/client';
import { formatDistance } from '../utils/distanceUtils';

const CATEGORY_ICONS = {
  'Attraction': FaLandmark,
  'Historic Site': FaMonument,
  'Park/Garden': FaTree,
  'Food & Drink': FaUtensils,
  'Natural Wonder': FaTree,
  'Local Shop': FaStore,
  'Point of Interest': FaMapMarkerAlt
};

const ExploreModal = ({
  isOpen,
  onClose,
  requestId,
  startLocation,
  endLocation,
  startCoords,
  endCoords,
  onAddToItinerary
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addingIndex, setAddingIndex] = useState(null);

  useEffect(() => {
    if (isOpen && startCoords && endCoords) {
      fetchSuggestions();
    }
  }, [isOpen, startCoords, endCoords]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await exploreBetweenPoints(requestId, startCoords, endCoords);
      const suggestions = result.suggestions || [];
      setSuggestions(suggestions);
      if (suggestions.length === 0) {
        // Check if this might be an API issue vs genuinely no results
        setError('No places found. The discovery service may be temporarily overloaded - please try again in a few minutes.');
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      if (err.response?.status === 504 || err.message?.includes('timeout')) {
        setError('The discovery service is currently overloaded. Please try again in a few minutes.');
      } else {
        setError('Failed to find suggestions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (suggestion, index) => {
    setAddingIndex(index);
    try {
      await onAddToItinerary(suggestion);
      // Remove the added suggestion from the list
      setSuggestions(prev => prev.filter((_, i) => i !== index));
    } catch (err) {
      console.error('Error adding to itinerary:', err);
    } finally {
      setAddingIndex(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaMapMarkerAlt className="text-amber-200" />
                Discover Places Between
              </h2>
              <p className="text-amber-100 text-sm mt-1">
                {startLocation} → {endLocation}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FaSpinner className="animate-spin text-4xl text-amber-500 mb-4" />
              <p className="text-neutral-600">Discovering nearby places...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchSuggestions}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : suggestions.length === 0 && !error ? (
            <div className="text-center py-12">
              <FaMapMarkerAlt className="text-5xl text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-600">No suggestions found for this route segment.</p>
              <p className="text-neutral-400 text-sm mt-2">Try a different segment or check back later.</p>
            </div>
          ) : suggestions.length === 0 && error ? (
            <div className="text-center py-12">
              <FaMapMarkerAlt className="text-5xl text-amber-300 mx-auto mb-4" />
              <p className="text-neutral-600 mb-4">{error}</p>
              <button
                onClick={fetchSuggestions}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion, index) => {
                const IconComponent = CATEGORY_ICONS[suggestion.category] || FaMapMarkerAlt;
                return (
                  <div
                    key={index}
                    className="border border-neutral-200 rounded-xl p-4 hover:border-amber-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <IconComponent className="text-amber-500" />
                          <h3 className="font-semibold text-neutral-800">{suggestion.name}</h3>
                        </div>
                        <p className="text-sm text-neutral-500 mb-2">
                          {suggestion.category}
                          {suggestion.subcategory && ` • ${suggestion.subcategory}`}
                          {' • '}
                          <span className="text-amber-600">{formatDistance(suggestion.distance_from_route_km)} from route</span>
                        </p>
                        {suggestion.description && (
                          <p className="text-sm text-neutral-600 mb-2">{suggestion.description}</p>
                        )}
                        {suggestion.opening_hours && (
                          <p className="text-xs text-neutral-400">Hours: {suggestion.opening_hours}</p>
                        )}
                        {suggestion.website && (
                          <a
                            href={suggestion.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 mt-1"
                          >
                            <FaExternalLinkAlt className="text-[10px]" /> Website
                          </a>
                        )}
                      </div>
                      <button
                        onClick={() => handleAdd(suggestion, index)}
                        disabled={addingIndex === index}
                        className={`flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                          addingIndex === index
                            ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                            : 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm hover:shadow-md'
                        }`}
                      >
                        {addingIndex === index ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <>
                            <FaPlus />
                            Add
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {suggestions.length > 0 && (
          <div className="border-t border-neutral-200 p-4 bg-neutral-50">
            <p className="text-xs text-neutral-500 text-center">
              Added places will be saved as waypoints. Re-optimize to incorporate them into your route.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExploreModal;
